import React, { useState, useEffect } from 'react';
import {
  Dialog,
  Transition
} from '@headlessui/react';
import {
  XMarkIcon,
  PlusIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { templateService, Template, CreateTemplateData, UpdateTemplateData, TemplateSection, TemplateField } from '../services/template';
import { useAuth } from '../hooks/useAuth';
import { toast } from '../utils/toast';

interface TemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  template?: Template | null;
  onSuccess: () => void;
}

const fieldTypes = [
  { value: 'text', label: 'Text Input', icon: '📝' },
  { value: 'textarea', label: 'Text Area', icon: '📄' },
  { value: 'select', label: 'Dropdown', icon: '📋' },
  { value: 'checkbox', label: 'Checkbox', icon: '☑️' },
  { value: 'date', label: 'Date', icon: '📅' },
  { value: 'number', label: 'Number', icon: '🔢' }
];

const templateCategories = [
  'General Medicine',
  'Cardiology',
  'Neurology',
  'Orthopedics',
  'Surgery',
  'Pediatrics',
  'Emergency Medicine',
  'Mental Health',
  'Other'
];

export const TemplateModal: React.FC<TemplateModalProps> = ({
  isOpen,
  onClose,
  template,
  onSuccess
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'basic' | 'sections'>('basic');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'personal' as 'organization' | 'personal',
    category: '',
    tags: [] as string[],
    version: '1.0.0'
  });
  
  const [sections, setSections] = useState<TemplateSection[]>([]);
  const [newTag, setNewTag] = useState('');

  const isAdmin = user?.role === 'admin';
  const isEditing = !!template;

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name,
        description: template.description,
        type: template.type === 'community' ? 'organization' : template.type,
        category: template.category,
        tags: [...template.tags],
        version: template.version
      });
      setSections([...template.sections]);
    } else {
      resetForm();
    }
  }, [template]);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      type: 'personal',
      category: '',
      tags: [],
      version: '1.0.0'
    });
    setSections([]);
    setActiveTab('basic');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validateForm = (): string[] => {
    const errors: string[] = [];
    
    if (!formData.name.trim()) errors.push('Template name is required');
    if (!formData.description.trim()) errors.push('Template description is required');
    if (!formData.category) errors.push('Category is required');
    if (sections.length === 0) errors.push('At least one section is required');
    
    sections.forEach((section, index) => {
      if (!section.title.trim()) errors.push(`Section ${index + 1} must have a title`);
      if (section.fields.length === 0) errors.push(`Section "${section.title}" must have at least one field`);
      
      section.fields.forEach((field, fieldIndex) => {
        if (!field.label.trim()) errors.push(`Field ${fieldIndex + 1} in section "${section.title}" must have a label`);
        if (field.type === 'select' && (!field.options || field.options.length === 0)) {
          errors.push(`Select field "${field.label}" must have options`);
        }
      });
    });
    
    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    if (errors.length > 0) {
      toast.error(`Please fix the following errors:\n${errors.join('\n')}`);
      return;
    }

    setLoading(true);
    try {
      const templateData = {
        ...formData,
        sections
      };

      if (isEditing && template) {
        await templateService.updateTemplate(template.id, templateData as UpdateTemplateData);
        toast.success('Template updated successfully');
      } else {
        await templateService.createTemplate(templateData as CreateTemplateData);
        toast.success('Template created successfully');
      }
      
      onSuccess();
      handleClose();
    } catch (error: any) {
      toast.error(error.message || 'Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const addSection = () => {
    const newSection: TemplateSection = {
      id: templateService.generateSectionId('New Section'),
      title: '',
      description: '',
      order: sections.length,
      fields: [],
      collapsed: false
    };
    setSections([...sections, newSection]);
    setActiveTab('sections');
  };

  const updateSection = (sectionIndex: number, updates: Partial<TemplateSection>) => {
    const newSections = [...sections];
    newSections[sectionIndex] = { ...newSections[sectionIndex], ...updates };
    setSections(newSections);
  };

  const deleteSection = (sectionIndex: number) => {
    if (confirm('Are you sure you want to delete this section?')) {
      const newSections = sections.filter((_, index) => index !== sectionIndex);
      setSections(newSections);
    }
  };

  const addField = (sectionIndex: number) => {
    const newField: TemplateField = {
      id: templateService.generateFieldId('New Field'),
      label: '',
      type: 'text',
      required: false,
      placeholder: '',
      defaultValue: ''
    };
    
    const newSections = [...sections];
    newSections[sectionIndex].fields.push(newField);
    setSections(newSections);
  };

  const updateField = (sectionIndex: number, fieldIndex: number, updates: Partial<TemplateField>) => {
    const newSections = [...sections];
    newSections[sectionIndex].fields[fieldIndex] = {
      ...newSections[sectionIndex].fields[fieldIndex],
      ...updates
    };
    setSections(newSections);
  };

  const deleteField = (sectionIndex: number, fieldIndex: number) => {
    if (confirm('Are you sure you want to delete this field?')) {
      const newSections = [...sections];
      newSections[sectionIndex].fields.splice(fieldIndex, 1);
      setSections(newSections);
    }
  };

  const moveSection = (fromIndex: number, toIndex: number) => {
    const newSections = [...sections];
    const [moved] = newSections.splice(fromIndex, 1);
    newSections.splice(toIndex, 0, moved);
    
    // Update order values
    newSections.forEach((section, index) => {
      section.order = index;
    });
    
    setSections(newSections);
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  return (
    <Transition appear show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-medium text-gray-900">
                    {isEditing ? 'Edit Template' : 'Create New Template'}
                  </h3>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-600"
                    onClick={handleClose}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Tabs */}
                <div className="border-b border-gray-200 mb-6">
                  <nav className="-mb-px flex space-x-8">
                    <button
                      onClick={() => setActiveTab('basic')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'basic'
                          ? 'border-indigo-500 text-indigo-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Basic Information
                    </button>
                    <button
                      onClick={() => setActiveTab('sections')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'sections'
                          ? 'border-indigo-500 text-indigo-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Template Sections ({sections.length})
                    </button>
                  </nav>
                </div>

                <div className="max-h-96 overflow-y-auto">
                  {activeTab === 'basic' && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Template Name *
                          </label>
                          <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="Enter template name"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Category *
                          </label>
                          <select
                            value={formData.category}
                            onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                          >
                            <option value="">Select a category</option>
                            {templateCategories.map(category => (
                              <option key={category} value={category}>{category}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Description *
                        </label>
                        <textarea
                          value={formData.description}
                          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                          rows={3}
                          className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="Describe what this template is used for"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Template Type
                          </label>
                          <select
                            value={formData.type}
                            onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as 'organization' | 'personal' }))}
                            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                            disabled={!isAdmin && formData.type === 'organization'}
                          >
                            <option value="personal">Personal Template</option>
                            {isAdmin && <option value="organization">Organization Template</option>}
                          </select>
                          <p className="mt-1 text-sm text-gray-500">
                            {formData.type === 'personal' 
                              ? 'Only you can edit this template. You can share it with your organization later.'
                              : 'All organization members can use this template. Only admins can edit.'
                            }
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Version
                          </label>
                          <input
                            type="text"
                            value={formData.version}
                            onChange={(e) => setFormData(prev => ({ ...prev, version: e.target.value }))}
                            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="1.0.0"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Tags
                        </label>
                        <div className="flex flex-wrap gap-2 mb-2">
                          {formData.tags.map(tag => (
                            <span
                              key={tag}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
                            >
                              {tag}
                              <button
                                type="button"
                                onClick={() => removeTag(tag)}
                                className="ml-1 text-indigo-600 hover:text-indigo-800"
                              >
                                <XMarkIcon className="h-3 w-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newTag}
                            onChange={(e) => setNewTag(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && addTag()}
                            className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="Add a tag and press Enter"
                          />
                          <button
                            type="button"
                            onClick={addTag}
                            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'sections' && (
                    <div className="space-y-6">
                      <div className="flex justify-between items-center">
                        <h4 className="text-md font-medium text-gray-900">Template Sections</h4>
                        <button
                          type="button"
                          onClick={addSection}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          <PlusIcon className="h-4 w-4 mr-2" />
                          Add Section
                        </button>
                      </div>

                      {sections.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <p>No sections created yet. Click "Add Section" to get started.</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {sections.map((section, sectionIndex) => (
                            <SectionBuilder
                              key={section.id}
                              section={section}
                              sectionIndex={sectionIndex}
                              onUpdateSection={updateSection}
                              onDeleteSection={deleteSection}
                              onAddField={addField}
                              onUpdateField={updateField}
                              onDeleteField={deleteField}
                              onMoveUp={sectionIndex > 0 ? () => moveSection(sectionIndex, sectionIndex - 1) : undefined}
                              onMoveDown={sectionIndex < sections.length - 1 ? () => moveSection(sectionIndex, sectionIndex + 1) : undefined}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={loading}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : (isEditing ? 'Update Template' : 'Create Template')}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

// Section Builder Component
interface SectionBuilderProps {
  section: TemplateSection;
  sectionIndex: number;
  onUpdateSection: (index: number, updates: Partial<TemplateSection>) => void;
  onDeleteSection: (index: number) => void;
  onAddField: (sectionIndex: number) => void;
  onUpdateField: (sectionIndex: number, fieldIndex: number, updates: Partial<TemplateField>) => void;
  onDeleteField: (sectionIndex: number, fieldIndex: number) => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}

const SectionBuilder: React.FC<SectionBuilderProps> = ({
  section,
  sectionIndex,
  onUpdateSection,
  onDeleteSection,
  onAddField,
  onUpdateField,
  onDeleteField,
  onMoveUp,
  onMoveDown
}) => {
  const [collapsed, setCollapsed] = useState(section.collapsed || false);

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setCollapsed(!collapsed)}
            className="text-gray-400 hover:text-gray-600"
          >
            {collapsed ? <ChevronDownIcon className="h-5 w-5" /> : <ChevronUpIcon className="h-5 w-5" />}
          </button>
          <input
            type="text"
            value={section.title}
            onChange={(e) => onUpdateSection(sectionIndex, { title: e.target.value })}
            className="text-lg font-medium border-none p-0 focus:ring-0 bg-transparent"
            placeholder="Section Title"
          />
        </div>
        
        <div className="flex items-center gap-2">
          {onMoveUp && (
            <button
              type="button"
              onClick={onMoveUp}
              className="text-gray-400 hover:text-gray-600"
              title="Move Up"
            >
              <ChevronUpIcon className="h-4 w-4" />
            </button>
          )}
          {onMoveDown && (
            <button
              type="button"
              onClick={onMoveDown}
              className="text-gray-400 hover:text-gray-600"
              title="Move Down"
            >
              <ChevronDownIcon className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => onDeleteSection(sectionIndex)}
            className="text-red-400 hover:text-red-600"
            title="Delete Section"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="space-y-4">
          <textarea
            value={section.description || ''}
            onChange={(e) => onUpdateSection(sectionIndex, { description: e.target.value })}
            rows={2}
            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Section description (optional)"
          />

          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <h5 className="text-sm font-medium text-gray-900">Fields</h5>
              <button
                type="button"
                onClick={() => onAddField(sectionIndex)}
                className="inline-flex items-center px-2 py-1 border border-gray-300 rounded text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <PlusIcon className="h-3 w-3 mr-1" />
                Add Field
              </button>
            </div>

            {section.fields.map((field, fieldIndex) => (
              <FieldBuilder
                key={field.id}
                field={field}
                fieldIndex={fieldIndex}
                onUpdateField={(updates) => onUpdateField(sectionIndex, fieldIndex, updates)}
                onDeleteField={() => onDeleteField(sectionIndex, fieldIndex)}
              />
            ))}

            {section.fields.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">
                No fields in this section. Click "Add Field" to get started.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Field Builder Component
interface FieldBuilderProps {
  field: TemplateField;
  fieldIndex: number;
  onUpdateField: (updates: Partial<TemplateField>) => void;
  onDeleteField: () => void;
}

const FieldBuilder: React.FC<FieldBuilderProps> = ({
  field,
  fieldIndex,
  onUpdateField,
  onDeleteField
}) => {
  const addOption = () => {
    const options = field.options || [];
    options.push('');
    onUpdateField({ options: [...options] });
  };

  const updateOption = (optionIndex: number, value: string) => {
    const options = [...(field.options || [])];
    options[optionIndex] = value;
    onUpdateField({ options });
  };

  const removeOption = (optionIndex: number) => {
    const options = [...(field.options || [])];
    options.splice(optionIndex, 1);
    onUpdateField({ options });
  };

  const fieldTypeInfo = fieldTypes.find(ft => ft.value === field.type);

  return (
    <div className="border border-gray-100 rounded p-3 bg-gray-50">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <input
          type="text"
          value={field.label}
          onChange={(e) => onUpdateField({ label: e.target.value })}
          className="text-sm border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="Field Label"
        />
        
        <select
          value={field.type}
          onChange={(e) => onUpdateField({ type: e.target.value as TemplateField['type'] })}
          className="text-sm border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
        >
          {fieldTypes.map(ft => (
            <option key={ft.value} value={ft.value}>
              {ft.icon} {ft.label}
            </option>
          ))}
        </select>

        <div className="flex items-center gap-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={field.required}
              onChange={(e) => onUpdateField({ required: e.target.checked })}
              className="text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <span className="ml-2 text-sm text-gray-700">Required</span>
          </label>
          <button
            type="button"
            onClick={onDeleteField}
            className="text-red-400 hover:text-red-600"
            title="Delete Field"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input
          type="text"
          value={field.placeholder || ''}
          onChange={(e) => onUpdateField({ placeholder: e.target.value })}
          className="text-sm border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="Placeholder text"
        />
        
        <input
          type="text"
          value={field.defaultValue || ''}
          onChange={(e) => onUpdateField({ defaultValue: e.target.value })}
          className="text-sm border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="Default value"
        />
      </div>

      {field.type === 'select' && (
        <div className="mt-3">
          <label className="block text-xs font-medium text-gray-700 mb-2">Options</label>
          <div className="space-y-2">
            {(field.options || []).map((option, optionIndex) => (
              <div key={optionIndex} className="flex gap-2">
                <input
                  type="text"
                  value={option}
                  onChange={(e) => updateOption(optionIndex, e.target.value)}
                  className="flex-1 text-sm border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder={`Option ${optionIndex + 1}`}
                />
                <button
                  type="button"
                  onClick={() => removeOption(optionIndex)}
                  className="text-red-400 hover:text-red-600"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addOption}
              className="text-sm text-indigo-600 hover:text-indigo-800"
            >
              + Add Option
            </button>
          </div>
        </div>
      )}

      {(field.type === 'text' || field.type === 'textarea') && (
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Min Length</label>
            <input
              type="number"
              value={field.validation?.minLength || ''}
              onChange={(e) => onUpdateField({ 
                validation: { 
                  ...field.validation, 
                  minLength: e.target.value ? parseInt(e.target.value) : undefined 
                } 
              })}
              className="text-sm border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500 w-full"
              min="0"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Max Length</label>
            <input
              type="number"
              value={field.validation?.maxLength || ''}
              onChange={(e) => onUpdateField({ 
                validation: { 
                  ...field.validation, 
                  maxLength: e.target.value ? parseInt(e.target.value) : undefined 
                } 
              })}
              className="text-sm border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500 w-full"
              min="1"
            />
          </div>
        </div>
      )}
    </div>
  );
};